export type AutomationStatus = "draft" | "active" | "paused" | "archived";
export type AutomationMode = "shadow" | "live";
export type RunStatus =
  | "pending"
  | "scheduled"
  | "running"
  | "completed"
  | "skipped"
  | "failed"
  | "cancelled";

export const STATUS_LABEL: Record<AutomationStatus, string> = {
  draft: "Draft",
  active: "Active",
  paused: "Paused",
  archived: "Archived",
};

export const STATUS_BADGE_CLASSES: Record<AutomationStatus, string> = {
  draft: "border-slate-200 bg-slate-50 text-slate-600",
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  paused: "border-amber-200 bg-amber-50 text-amber-700",
  archived: "border-slate-200 bg-slate-50 text-slate-500",
};

export const STATUS_DOT_CLASSES: Record<AutomationStatus, string> = {
  draft: "bg-slate-400",
  active: "bg-emerald-500",
  paused: "bg-amber-500",
  archived: "bg-slate-300",
};

/**
 * Mode is deliberately louder than status.
 *
 * "Live" is the only state in which an automation can send anything to a customer, so
 * it reads as a warning colour rather than a success one — an admin scanning the list
 * should be able to see at a glance which automations can actually email people.
 */
export const MODE_LABEL: Record<AutomationMode, string> = {
  shadow: "Shadow",
  live: "Live",
};

export const MODE_BADGE_CLASSES: Record<AutomationMode, string> = {
  shadow: "border-sky-200 bg-sky-50 text-sky-700",
  live: "border-rose-200 bg-rose-50 text-rose-700",
};

export const MODE_DOT_CLASSES: Record<AutomationMode, string> = {
  shadow: "bg-sky-500",
  live: "bg-rose-500",
};

export const RUN_STATUS_LABEL: Record<RunStatus, string> = {
  pending: "Pending",
  scheduled: "Scheduled",
  running: "Running",
  completed: "Sent",
  skipped: "Skipped",
  failed: "Failed",
  cancelled: "Cancelled",
};

export const RUN_STATUS_BADGE_CLASSES: Record<RunStatus, string> = {
  pending: "border-slate-200 bg-slate-50 text-slate-600",
  scheduled: "border-sky-200 bg-sky-50 text-sky-700",
  running: "border-sky-200 bg-sky-50 text-sky-700",
  completed: "border-emerald-200 bg-emerald-50 text-emerald-700",
  // Amber, not red: a skip is usually correct (conditions not met, shadow mode) but is
  // also how a misconfigured automation looks, so it should invite a glance.
  skipped: "border-amber-200 bg-amber-50 text-amber-700",
  failed: "border-red-200 bg-red-50 text-red-700",
  cancelled: "border-slate-200 bg-slate-50 text-slate-500",
};

export const RUN_STATUS_DOT_CLASSES: Record<RunStatus, string> = {
  pending: "bg-slate-400",
  scheduled: "bg-sky-500",
  running: "bg-sky-500",
  completed: "bg-emerald-500",
  skipped: "bg-amber-500",
  failed: "bg-red-500",
  cancelled: "bg-slate-300",
};

export function toAutomationStatus(status?: string | null): AutomationStatus {
  return status && status in STATUS_LABEL ? (status as AutomationStatus) : "draft";
}

export function toAutomationMode(mode?: string | null): AutomationMode {
  return mode === "live" ? "live" : "shadow";
}

export function toRunStatus(status?: string | null): RunStatus {
  return status && status in RUN_STATUS_LABEL ? (status as RunStatus) : "pending";
}
